// Ordering for a definition's node attributes (KEY-244). Moved here from web-admin (KEY-560) so the user app's builder orders
// fields by the same rules the admin editor does; web-admin re-exports it from its old path.
//
// `order` is stamped from array index alone — every mutation (add, delete, field edit, move) must go
// through `restampNodeAttributeOrder` so the stored value and the array position can never diverge. Before
// this module existed, `order` was only stamped at add-time as `nodeAttributes.length`; a delete followed
// by an add left two attributes sharing the same `order`, and since neither the API load nor the render
// path had a stable tiebreaker, ties could render in a different order between requests.
//
// `Order` is one sequence across every target (Section/Segment/Item), but the editor and the rendered dial
// group attributes by target — so a move is scoped to the attribute's own group. Moving the first Section
// attribute "up" must not jump it in front of an Item attribute; it's simply not movable further.
import type { GeneratedNodeAttribute } from "@keydials/api-client";

export function restampNodeAttributeOrder(attrs: GeneratedNodeAttribute[]): GeneratedNodeAttribute[] {
  return attrs.map((a, i) => ({ ...a, order: i }));
}

/** Indices (into `attrs`) of every attribute sharing `target`, in array order. */
function groupIndices(attrs: GeneratedNodeAttribute[], target: GeneratedNodeAttribute["target"]): number[] {
  const indices: number[] = [];
  attrs.forEach((a, i) => {
    if (a.target === target) indices.push(i);
  });
  return indices;
}

const titleOf = (a: GeneratedNodeAttribute): string | null => a.fieldsetTitle?.trim() || null;

/** Rewrites one target band into the given order of source indices, restamping `order` from the result. */
function reorderBand(
  attrs: GeneratedNodeAttribute[],
  target: GeneratedNodeAttribute["target"],
  bandOrder: number[],
): GeneratedNodeAttribute[] {
  const band = groupIndices(attrs, target);
  const values = bandOrder.map((i) => attrs[i]);
  const next = [...attrs];
  band.forEach((position, k) => { next[position] = values[k]; });
  return restampNodeAttributeOrder(next);
}

/**
 * One orderable unit within a target band (KEY-551): a run of fields sharing an authored group heading, or
 * a single ungrouped field. Blocks are what a reader actually sees down the page — one card per group,
 * loose fields between them — so they, not raw array positions, are what ordering moves.
 */
export interface AttributeBlock {
  /** The group heading, or null for a lone ungrouped field. */
  title: string | null;
  /** Indices into the caller's array, in band order. */
  indices: number[];
}

/**
 * The blocks of one target band, in order. Consecutive fields sharing a heading form one block; every
 * ungrouped field is a block of its own.
 *
 * A group whose members are NOT consecutive yields two blocks with the same title — which is exactly what
 * the user app would draw as one card pulling fields in from below. `normaliseAttributeFieldsetRuns` is what stops
 * that happening from the editor; it can still arrive by import or spreadsheet.
 */
export function attributeBlocks(
  attrs: GeneratedNodeAttribute[],
  target: GeneratedNodeAttribute["target"],
): AttributeBlock[] {
  const blocks: AttributeBlock[] = [];
  for (const i of groupIndices(attrs, target)) {
    const title = titleOf(attrs[i]);
    const last = blocks[blocks.length - 1];
    if (title !== null && last && last.title === title) last.indices.push(i);
    else blocks.push({ title, indices: [i] });
  }
  return blocks;
}

/**
 * Gathers every group's members into one consecutive run, at the position of the group's FIRST member.
 *
 * Contiguity is what makes ordering mean anything: without it "move this group down" has no single thing
 * to move, and a field dropped into the middle of a group's span would be pulled into that group's card
 * without being in the group. Called when a field is assigned to a group, so the array always matches what
 * the user app will draw.
 *
 * `onlyTarget` limits the work to one level's band (the builder normalises the level being edited); every
 * level is normalised without it. Web-admin knows this as `normaliseFieldsetRuns`; the package names it for
 * the array, because the builder's model-level edit of that name is in `fieldOps`.
 */
export function normaliseAttributeFieldsetRuns(attrs: GeneratedNodeAttribute[], onlyTarget?: GeneratedNodeAttribute["target"]): GeneratedNodeAttribute[] {
  let next = [...attrs];
  const targets = onlyTarget === undefined ? [...new Set(next.map((a) => a.target))] : [onlyTarget];
  for (const target of targets) {
    const band = groupIndices(next, target);
    const drawn = new Set<string>();
    const grouped: number[] = [];
    const loose: number[] = [];
    for (const i of band) {
      const title = titleOf(next[i]);
      if (title === null) { loose.push(i); continue; }
      if (drawn.has(title)) continue; // already pulled in at the group's first member
      drawn.add(title);
      grouped.push(...band.filter((j) => titleOf(next[j]) === title));
    }
    // KEY-558: groups first, then the loose fields — the same rule the user app draws by
    // (`partitionFieldsets`). Keeping the array in the drawn order is what lets the side list reorder by
    // index at all: what an author drags into place here is literally what a reader meets.
    next = reorderBand(next, target, [...grouped, ...loose]);
  }
  return next;
}

/**
 * The blocks of a band, split the way they are DRAWN since KEY-558: every group first, then the loose
 * fields. Moves are bounded inside one half or the other — a group cannot be pushed below a loose field,
 * and a loose field cannot be pushed above a group, because the rule that groups come first would simply
 * undo it on the next commit and the arrow would look broken.
 */
function partitionedBlocks(attrs: GeneratedNodeAttribute[], target: GeneratedNodeAttribute["target"]) {
  const blocks = attributeBlocks(attrs, target);
  return {
    grouped: blocks.filter((b) => b.title !== null),
    loose: blocks.filter((b) => b.title === null),
  };
}

/** Rebuilds a band from its two halves, keeping groups-before-loose whatever the move was. */
function bandFrom(grouped: AttributeBlock[], loose: AttributeBlock[]): number[] {
  return [...grouped, ...loose].flatMap((b) => b.indices);
}

/** Whether the attribute at `index` can move `delta` places (±1) among its peers. */
export function canMoveNodeAttribute(attrs: GeneratedNodeAttribute[], index: number, delta: number): boolean {
  const { grouped, loose } = partitionedBlocks(attrs, attrs[index].target);
  if (titleOf(attrs[index]) !== null) {
    // Inside its own card: a grouped field orders against its fellow members and nothing else.
    const block = grouped.find((b) => b.indices.includes(index));
    if (!block) return false;
    const to = block.indices.indexOf(index) + delta;
    return to >= 0 && to < block.indices.length;
  }
  const at = loose.findIndex((b) => b.indices.includes(index));
  const to = at + delta;
  return at >= 0 && to >= 0 && to < loose.length;
}

/**
 * Moves the attribute at `index` by `delta` places (±1) among its peers — within its group when it is in
 * one, otherwise among the other loose fields — restamping `order` from the result. A move that would
 * leave its half of the band is a no-op: same array back.
 */
export function moveNodeAttribute(attrs: GeneratedNodeAttribute[], index: number, delta: number): GeneratedNodeAttribute[] {
  if (!canMoveNodeAttribute(attrs, index, delta)) return attrs;
  const target = attrs[index].target;
  const { grouped, loose } = partitionedBlocks(attrs, target);

  if (titleOf(attrs[index]) !== null) {
    const next = grouped.map((b) => {
      if (!b.indices.includes(index)) return b;
      const inner = [...b.indices];
      const at = inner.indexOf(index);
      [inner[at], inner[at + delta]] = [inner[at + delta], inner[at]];
      return { ...b, indices: inner };
    });
    return reorderBand(attrs, target, bandFrom(next, loose));
  }

  const next = [...loose];
  const at = next.findIndex((b) => b.indices.includes(index));
  [next[at], next[at + delta]] = [next[at + delta], next[at]];
  return reorderBand(attrs, target, bandFrom(grouped, next));
}

/** Whether the group `title` can move `delta` places (±1) among the OTHER GROUPS at its level. */
export function canMoveFieldset(
  attrs: GeneratedNodeAttribute[],
  target: GeneratedNodeAttribute["target"],
  title: string,
  delta: number,
): boolean {
  const { grouped } = partitionedBlocks(attrs, target);
  const at = grouped.findIndex((b) => b.title === title.trim());
  if (at < 0) return false;
  const to = at + delta;
  return to >= 0 && to < grouped.length;
}

/**
 * Moves a whole group past the group above or below it, carrying every member. This is the move the
 * per-field arrows deliberately cannot make. Loose fields are not passed over — they always come after
 * every group — so a one-group level has nowhere to move.
 */
export function moveFieldset(
  attrs: GeneratedNodeAttribute[],
  target: GeneratedNodeAttribute["target"],
  title: string,
  delta: number,
): GeneratedNodeAttribute[] {
  if (!canMoveFieldset(attrs, target, title, delta)) return attrs;
  const { grouped, loose } = partitionedBlocks(attrs, target);
  const next = [...grouped];
  const at = next.findIndex((b) => b.title === title.trim());
  [next[at], next[at + delta]] = [next[at + delta], next[at]];
  return reorderBand(attrs, target, bandFrom(next, loose));
}
